import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Globe, ArrowRight, MessageSquare, Building2, Users, Shield, CheckCircle, AlertTriangle, Headphones } from 'lucide-react';

// Declare global recaptcha type if not already declared
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

const Contact = () => {
  // State for email subject line
  const [emailSubject, setEmailSubject] = useState<string>("Contact Form Submission");
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [showRecaptchaError, setShowRecaptchaError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Load reCAPTCHA script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    
    window.onRecaptchaLoad = () => {
      setRecaptchaLoaded(true);
    };
    
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
      (window as any).onRecaptchaLoad = undefined;
    };
  }, []);
  
  // Initialize reCAPTCHA when it's loaded
  useEffect(() => {
    if (recaptchaLoaded && window.grecaptcha) {
      try {
        window.grecaptcha.render('contact-recaptcha-container', {
          sitekey: '6LeWfxorAAAAAHuXucPcVo0Rd0nCrIkXjezFDR8e'
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  }, [recaptchaLoaded]);
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setShowRecaptchaError(false);
    
    // Verify reCAPTCHA
    if (window.grecaptcha) {
      const recaptchaResponse = window.grecaptcha.getResponse();
      if (!recaptchaResponse) {
        setShowRecaptchaError(true);
        return;
      }
      
      // If reCAPTCHA verification passed, submit the form
      const form = e.target as HTMLFormElement;
      
      // Create a FormData object to get the form data
      const formDataObj = new FormData(form);
      
      // Submit the form using fetch
      fetch(form.action, {
        method: form.method,
        body: formDataObj,
        headers: {
          Accept: 'application/json'
        }
      })
        .then(response => {
          if (response.ok) {
            // Show thank you message
            setFormSubmitted(true);
            // Reset email subject
            setEmailSubject("Contact Form Submission");
          } else {
            console.error('Form submission failed');
            alert('There was a problem submitting your form. Please try again.');
          }
        })
        .catch(error => {
          console.error('Form submission error:', error);
          alert('There was a problem submitting your form. Please try again.');
        });
    } else {
      console.error('reCAPTCHA not loaded');
      setShowRecaptchaError(true);
    }
  };
  
  const contactMethods = [
    {
      icon: Phone,
      title: "Call Us",
      primary: "(888) 991-7050",
      secondary: "24/7 Support Available",
      action: "tel:+18889917050"
    },
    {
      icon: Mail,
      title: "Email Us",
      primary: "contact@astronshield.com",
      secondary: "We'll respond within 24 hours",
      action: "mailto:contact@astronshield.com"
    },
    {
      icon: Building2,
      title: "Visit Us",
      primary: "Cleveland, OH",
      secondary: "United States",
      action: "https://maps.google.com/?q=Cleveland,OH"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Secure Communication",
      description: "All communications are encrypted and protected"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Direct access to our security professionals"
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "Round-the-clock emergency assistance"
    },
    {
      icon: Globe,
      title: "Global Coverage",
      description: "Support across multiple time zones"
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <MessageSquare className="h-16 w-16 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            <span className="text-gradient">Contact Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-2xl mx-auto"
          >
            Get in touch with our security experts to discuss your cybersecurity needs
          </motion.p>
        </div>

        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.action}
              className="glass-panel rounded-xl p-8 hover-card transition-transform hover:scale-105"
              target={method.icon === Building2 ? "_blank" : undefined}
              rel={method.icon === Building2 ? "noopener noreferrer" : undefined}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-xl mb-6 mx-auto">
                <method.icon className="h-8 w-8 text-white" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">{method.title}</h3>
                <p className="text-lg text-primary mb-1">{method.primary}</p>
                <p className="text-sm text-muted">{method.secondary}</p>
              </div>
            </a>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="glass-panel rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6">Global Headquarters</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <Building2 className="h-6 w-6 text-primary mt-1" />
                  <div className="ml-4">
                    <p className="font-medium">Address</p>
                    <p className="text-lg">
                      Cleveland, OH<br />
                      United States
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-primary mt-1" />
                  <div className="ml-4">
                    <p className="font-medium">Hours of Operation</p>
                    <p className="text-lg">
                      Monday - Friday: 9:00 AM - 5:00 PM EST<br />
                      24/7 Emergency Support Available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-panel rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6">Emergency Support</h2>
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-red-500/10 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-red-500">24/7 Emergency Hotline</p>
                  <a href="tel:+18889917050" className="text-lg text-primary hover:text-accent transition-colors">
                    (888) 991-7050
                  </a>
                </div>
              </div>
            </div>

            <div className="glass-panel rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6">Support Channels</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Headphones className="h-6 w-6 text-primary" />
                  <span className="text-muted">Live Chat Support</span>
                </div>
                <div className="flex items-center space-x-4">
                  <Mail className="h-6 w-6 text-primary" />
                  <span className="text-muted">Email Support</span>
                </div>
                <div className="flex items-center space-x-4">
                  <Phone className="h-6 w-6 text-primary" />
                  <span className="text-muted">Phone Support</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="glass-panel rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>
              
              {formSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center p-8 glass-panel rounded-xl border-2 border-primary"
                >
                  <CheckCircle className="h-16 w-16 text-primary mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Thank You for Reaching Out!</h3>
                  <p className="text-lg text-muted mb-6">
                    We've received your message and will respond to your inquiry as soon as possible. Our team is dedicated to providing you with the best cybersecurity support.
                  </p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="btn-primary px-8"
                  >
                    <span className="flex items-center justify-center">
                      Send Another Message
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                </motion.div>
              ) : (
                <form action="https://formspree.io/f/meoadbrl" method="POST" className="space-y-6" onSubmit={handleSubmit}>
                  <input type="hidden" name="_subject" value={emailSubject} />
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      id="subject-field"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                      placeholder="How can we help?"
                      onChange={(e) => {
                        // Update the _subject hidden field when the subject changes
                        setEmailSubject(e.target.value ? `Contact: ${e.target.value}` : 'Contact Form Submission');
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      name="message"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                      rows={4}
                      placeholder="Your message..."
                      required
                    />
                  </div>
                  
                  {/* reCAPTCHA */}
                  <div className="flex flex-col items-center">
                    <div id="contact-recaptcha-container"></div>
                    {showRecaptchaError && (
                      <p className="text-red-500 mt-2">Please complete the reCAPTCHA before submitting.</p>
                    )}
                  </div>

                  <button type="submit" className="w-full btn-primary">
                    <span className="flex items-center justify-center">
                      Send Message
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Contact Us</h2>
            <p className="text-lg text-muted">Experience our commitment to excellence in customer service</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="glass-panel rounded-xl p-6 hover-card">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;